/**
 * Telegram Mini App auto-auth bridge.
 *
 * When the website is opened inside `@roamzy_bot` (a TG Web App), the
 * `telegram-web-app.js` SDK populates `window.Telegram.WebApp.initData`
 * with a signed query string. We POST it to `/api/auth/telegram-webapp`,
 * which HMAC-verifies and sets a session cookie. No "Sign in" prompt
 * needed.
 *
 * Outside Telegram (regular browser), `window.Telegram` is undefined and
 * this module exits silently — the normal Login Widget / Google flow
 * handles auth.
 */
import { Auth } from './auth';

interface TelegramWebApp {
  initData?: string;
  ready?: () => void;
  expand?: () => void;
  close?: () => void;
  themeParams?: Record<string, string>;
  colorScheme?: 'light' | 'dark';
  isExpanded?: boolean;
  version?: string;
  platform?: string;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

/**
 * Returns true if we're running inside Telegram (Mini App context).
 * Used by the nav to hide the "Sign in" button — the user is auto-authed
 * already; surfacing a CTA they shouldn't need is confusing.
 *
 * Note: this is sync. With `defer` on the SDK script (not `async`) it's
 * guaranteed to be loaded before main.ts modules run, so this read is
 * race-free in normal page load. The async polling fallback below
 * handles the rare degenerate case (e.g. cached DNS failure delaying
 * the SDK fetch past first paint).
 */
export function isInTelegram(): boolean {
  return !!(window.Telegram?.WebApp?.initData && window.Telegram.WebApp.initData.length > 0);
}

/**
 * Wait up to `maxMs` for the Telegram SDK to populate `window.Telegram.WebApp`.
 * Returns the WebApp object on success, null on timeout. Defensive: with
 * `defer` script the SDK is normally ready by the time we call this, but a
 * slow-network scenario could still cause it to lag — this prevents the
 * auto-auth from silently no-op'ing in that case.
 */
async function waitForTelegramSdk(maxMs = 1500): Promise<TelegramWebApp | null> {
  const start = Date.now();
  while (!window.Telegram?.WebApp?.initData && Date.now() - start < maxMs) {
    await new Promise((r) => setTimeout(r, 50));
  }
  return window.Telegram?.WebApp ?? null;
}

/**
 * Attempts auto-auth via Mini App initData. No-op outside Telegram.
 * Returns true on success (session set), false otherwise.
 *
 * Called once at boot, after Auth.init() has run. If user already has a
 * valid session cookie (returning visitor), this is redundant but harmless —
 * backend just returns existing user. If not, this creates a session.
 */
export async function autoAuthFromTelegram(): Promise<boolean> {
  // Belt-and-braces: even though the SDK script is `defer`-loaded (so it
  // executes before main.ts), polling here handles the rare case where
  // the SDK download hadn't finished by the time main.ts started — e.g.
  // first visit on a slow connection. Inside Telegram the SDK is usually
  // cached by the host client so this resolves immediately.
  const tg = await waitForTelegramSdk();
  if (!tg || !tg.initData) return false;

  // Tell Telegram our app is ready (collapses splash, expands viewport)
  try {
    tg.ready?.();
    tg.expand?.();
  } catch {
    /* SDK old, just continue */
  }

  // If Auth.init already established a session via existing cookie, skip
  if (Auth.isAuthed()) return true;

  try {
    // Lazy import to avoid pulling utm helpers when not in Telegram
    const { authPayloadExtras } = await import('./utm');
    const res = await fetch('/api/auth/telegram-webapp', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ initData: tg.initData, ...authPayloadExtras() }),
    });
    if (!res.ok) {
      if (import.meta.env.DEV) {
        console.warn(`[tg-webapp] auto-auth failed: HTTP ${res.status}`);
      }
      return false;
    }
    // Re-pull /api/me so Auth state updates from the freshly-set cookie
    await Auth.init();
    return Auth.isAuthed();
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[tg-webapp] auto-auth error:', err);
    }
    return false;
  }
}
